// A Non-Penyedia PBJ project's home (/project/:id for a project of kind
// "pbj", migration 0023). The same container split as the paket home: the
// PBJ column on the left, the project column (Chat di project ini, File
// project) on the right from a 960px main, one scroller below that.
//
//   header      Paket PBJ with the current stage badge, the name (h1), the
//               badges (metode or tipe, TA, kode RUP, progres %), the facts
//               (jalur, unit kerja, jenis pengadaan, pagu, aktivitas
//               selesai), Ubah paket (pbj/PaketDialog) and Lihat alur (Alur
//               PBJ on the method's or type's diagram, this project's ticks
//               marked done); the moreH Menu (Ganti nama, Backup project: the
//               paket and its ticks as a .json file (./backup.ts), Hapus)
//   stepper     the six PBJ stages as buttons with done/total each, or
//               "Tidak berlaku" for a stage without activities (Swakelola
//               pemilihan): picking one shows its activities; the current
//               stage carries the brand dot; under it the note that drafting
//               in order is advised, not required (../project/ActivityDocs.tsx)
//   summary     the viewed stage's progress bar, and the current role
//               (Mode) with its access: can tick some activities, or view only
//               (one compact strip under the stage head)
//   next        Berikutnya untuk Anda: the first activity of the viewed stage
//               the role still has to do, with Buat dokumen and Buat dengan arahan
//               for its output (the draft opens in the right panel)
//   grouping    the activities as Aktivitas Anda, then Pihak lain (read only,
//               named once in the group title instead of on every row)
//   activities  the stage's activities from the dataset, filtered by the
//               route. A checkbox only for the actor role (the API enforces
//               it too); every other role reads it with "Dicentang oleh"
//               or "Dicentang oleh <pelaku>". Detail opens the node's facts
//               (./NodeFacts.tsx, simpul selanjutnya named by activity).
//               "Lihat alur tahap ini" opens the stage's diagram. The
//               header's "Download gabungan semua dokumen" downloads the whole
//               project's saved documents in one Word file
//               (../project/MergeDocs.tsx); "Download gabungan dokumen tahap
//               ini" sits in the stage's tool row under its heading.
//               "Hanya aktivitas peran saya" filters the list. A decision
//               node carries "Titik keputusan"; one with an output has
//               Buat dokumen and Buat dengan arahan (the draft opens in the right
//               panel, ../project/ActivityDocs.tsx), Unggah dokumen (a Word,
//               PDF or Excel file as its next saved version, straight from
//               the row) and Unduh .docx (the Word skeleton with the paket
//               data).
//               An activity no Non-Penyedia role acts in (the penyedia's,
//               the peserta's, another party's) is "Di luar Pluto": read
//               only, explained, and out of every done/total.
//   rka         RKA paket under the header facts (./Rka.tsx): upload,
//               replace, download, delete; read by stage 0 and 1 drafts
//   shared      a member (access "member", the project was sent to them)
//               sees Dibagikan in the header; Anggota project in the menu
//               (./Members.tsx)
//   handover    stage 0 only: Kirim dokumen tahap 0 (PA/KPA) and its history
//               (./Handover.tsx)
//   panel       from 1024px the right panel's left edge drags wider or
//               narrower (../project/PanelResizer.tsx)
//   actions     Jadikan tahap berjalan for a viewed stage that is not the
//               current one and applies; then the note that a tick is no
//               legal approval
//   chat       the project chat (ChatScreen with projectId); its composer
//              renders in the dock pinned under the PBJ column (.pj-dock)
import { useEffect, useId, useMemo, useState, type ReactNode } from "react";
import * as API from "../api";
import type { PbjActivity, PbjProjectDetail, PbjStage, Project } from "../api";
import { useUser } from "../auth";
import { ROLE_LABEL } from "../lib/labels";
import { ChatScreen } from "../screens/Chat";
import { firstName } from "../project/Paket";
import { ProjectChats } from "../project/ProjectChats";
import { ProjectFilesPanel } from "../project/ProjectFiles";
import { pbjStageChips, projectDraftExample } from "../project/stage-chips";
import { ActivityDocButtons, ActivityDocPanelSlot, ActivityDocStatus, ActivityDocUpload, ActivityDocsProvider, SequenceNote, canActivityDocs } from "../project/ActivityDocs";
import { PANEL_ID, PanelHead, PanelToggle, useSidePanel } from "../project/SidePanel";
import { navigate } from "../router";
import { useFeatures } from "../shell/features";
import type { ProjectsApi } from "../shell/Projects";
import { Badge, Notice, SkeletonRows, Switch, useAsync } from "../ui/components";
import { Glyph } from "../ui/icons";
import { Menu } from "../ui/Menu";
import { formatRupiah } from "../lib/format";
import { JENIS_LABEL } from "../project/stages";
import { methodText, OUTSIDE_PLUTO_HINT, PBJ_STAGE_HINT, PBJ_STAGE_LABEL, PBJ_STAGE_ORDER, pbjStageApplies, progressPct, routeFlowPath, routeText, stageFlowPath } from "./labels";
import { backupFileName, downloadPbjBackup } from "./backup";
import { canDocx, useDocx } from "./docx";
import { NodeFacts } from "./NodeFacts";
import { PaketDialog } from "./PaketDialog";
import { HandoverSection } from "./Handover";
import { MembersDialog } from "./Members";
import { PanelResizer } from "../project/PanelResizer";
import { RkaBlock } from "./Rka";
import { MergeDocsButton, canMergeDocs } from "../project/MergeDocs";

export interface PbjProjectProps {
  project: Project;
  projects: ProjectsApi;
}

export function PbjProject({ project: listed, projects }: PbjProjectProps) {
  const detail = useAsync<PbjProjectDetail>(() => API.getPbjProject(listed.id), [listed.id]);
  const [panelOpen] = useSidePanel();
  const [dock, setDock] = useState<HTMLDivElement | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [membersOpen, setMembersOpen] = useState(false);
  const [backup, setBackup] = useState<{ busy: boolean; error: string | null; saved: string | null }>({ busy: false, error: null, saved: null });
  const flowOn = useFeatures().has("pbj_flow");
  const user = useUser();
  // The role decides the tick rights: a switch from the Mode menu reloads them.
  useEffect(() => {
    if (detail.data && detail.data.role !== user.role) detail.reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.role]);

  let body: ReactNode;
  if (detail.loading && !detail.data) body = <SkeletonRows count={4} label="Memuat paket PBJ" />;
  else if (detail.error && !detail.data) {
    body = (
      <Notice
        tone="danger"
        title="Paket PBJ belum termuat"
        action={
          <button type="button" className="btn btn-secondary" onClick={detail.reload}>
            Coba lagi
          </button>
        }
      >
        <p>{API.errorMessage(detail.error)}</p>
      </Notice>
    );
  } else if (detail.data) {
    body = <PbjFlow detail={detail.data} onChange={detail.set} />;
  }

  const saveBackup = async () => {
    if (backup.busy) return;
    setBackup({ busy: true, error: null, saved: null });
    try {
      const file = await downloadPbjBackup(listed.id);
      setBackup({ busy: false, error: null, saved: backupFileName(file.project.name, file.exportedAt) });
    } catch (err) {
      setBackup({ busy: false, error: API.errorMessage(err), saved: null });
    }
  };

  const data = detail.data;
  const member = data?.project.access === "member";
  // A member cannot rename or delete the owner's project; Anggota project is for both.
  const menu = [
    ...(member ? [] : [{ id: "rename", label: "Ganti nama", icon: "edit2" as const, onSelect: () => projects.openRename(listed) }]),
    { id: "members", label: "Anggota project", icon: "profile2user" as const, onSelect: () => setMembersOpen(true) },
    { id: "backup", label: "Backup project", icon: "documentDownload" as const, onSelect: () => void saveBackup() },
    ...(member ? [] : [{ id: "delete", label: "Hapus", icon: "trash" as const, tone: "danger" as const, onSelect: () => projects.openDelete(listed) }]),
  ];
  return (
    <ActivityDocsProvider projectId={listed.id}>
    <div className="pj pk pbj" data-view="pbj" data-panel={panelOpen ? "open" : "closed"}>
      <div className="pk-layout">
        <div className="pk-main" data-ui-scroller="">
          <div className="pk-section">
            <header className="pk-head" aria-labelledby="pbj-title">
              <div className="pk-head-row">
                <p className="pk-kind">
                  <Glyph name="building" size={18} />
                  <span>Paket PBJ</span>
                  {data ? <Badge tone="info">Tahap {PBJ_STAGE_LABEL[data.project.stage]}</Badge> : null}
                  {member ? <Badge>Dibagikan</Badge> : null}
                </p>
                <span className="pj-head-actions">
                  {data ? <PbjHeadActions project={data.project} flowOn={flowOn} onEdit={() => setEditOpen(true)} /> : null}
                  <PanelToggle />
                  <Menu
                    label={`Tindakan untuk ${listed.name}`}
                    align="end"
                    className="pj-menu"
                    items={menu}
                  />
                </span>
              </div>
              <h1 id="pbj-title" className="pk-title">
                {listed.name}
              </h1>
              {data ? <PbjHeadFacts project={data.project} onEdit={() => setEditOpen(true)} /> : null}
              {member ? <p className="pbj-next">Project ini dibagikan kepada Anda oleh pemiliknya melalui Kirim dokumen Rencana Kerja & Anggaran.</p> : null}
              <RkaBlock projectId={listed.id} />
              {backup.busy ? (
                <p className="pbj-next" role="status">
                  Menyiapkan backup project
                </p>
              ) : backup.saved ? (
                <p className="pbj-next" role="status">
                  Backup tersimpan sebagai {backup.saved}. Berisi data paket dan aktivitas yang sudah dicentang.
                </p>
              ) : null}
              {backup.error ? (
                <Notice tone="danger" title="Backup belum tersimpan">
                  <p>{backup.error}</p>
                </Notice>
              ) : null}
            </header>
            {data ? (
              <PaketDialog
                open={editOpen}
                project={data.project}
                onClose={() => setEditOpen(false)}
                onSaved={(next) => {
                  detail.set(next);
                  setEditOpen(false);
                }}
              />
            ) : null}
            <MembersDialog projectId={listed.id} open={membersOpen} onClose={() => setMembersOpen(false)} onLeft={() => navigate("/project-pbj")} />
          </div>
          <div className="pk-section">{body}</div>
          <div className="pj-chat">
            <ChatScreen
              projectId={listed.id}
              projectName={listed.name}
              greeting="Tanya Pluto tentang paket ini"
              greetingLevel="h2"
              // The paket's own stage tasks, never a generic example.
              suggestions={pbjStageChips(data?.project.stage, listed.name)}
              draftExample={projectDraftExample(user.role, listed.name)}
              dock={dock}
              greetingMessage={{
                text: data
                  ? `Halo ${firstName(user.name)}, paket ${listed.name} sedang di tahap ${PBJ_STAGE_LABEL[data.project.stage]}. Tanyakan langkah, dokumen, atau regulasinya.`
                  : `Halo ${firstName(user.name)}, ini paket ${listed.name}. Tanyakan langkah, dokumen, atau regulasinya.`,
              }}
            />
          </div>
        </div>
        {panelOpen ? <PanelResizer /> : null}
        <aside id={PANEL_ID} className="pj-aside pk-aside" aria-label="Isi project" data-ui-scroller="">
          <PanelHead />
          <ActivityDocPanelSlot>
            <ProjectChats projectId={listed.id} />
            <ProjectFilesPanel projectId={listed.id} />
          </ActivityDocPanelSlot>
        </aside>
        <div ref={setDock} className="pj-dock no-print" />
      </div>
    </div>
    </ActivityDocsProvider>
  );
}

/** Ubah paket, Lihat alur and Download gabungan semua dokumen, small, in the header row beside the panel toggle. */
function PbjHeadActions({ project: p, flowOn, onEdit }: { project: PbjProjectDetail["project"]; flowOn: boolean; onEdit: () => void }) {
  const mergeOn = canMergeDocs(useFeatures());
  return (
    <span className="pbj-head-actions">
      <button type="button" className="btn btn-secondary btn-sm" onClick={onEdit}>
        <Glyph name="edit2" size={16} />
        <span>Ubah paket</span>
      </button>
      {flowOn ? (
        <a className="btn btn-secondary btn-sm" href={routeFlowPath(p)}>
          <Glyph name="layers" size={16} />
          <span>Lihat alur</span>
        </a>
      ) : null}
      {mergeOn ? <MergeDocsButton projectId={p.id} label="Download gabungan semua dokumen" size="sm" modeHint="title" /> : null}
    </span>
  );
}

/** One fact; an empty value reads muted. */
function Fact({ label, value, empty, num }: { label: string; value: ReactNode; empty?: boolean; num?: boolean }) {
  return (
    <div className="pk-fact" data-empty={empty ? "" : undefined}>
      <dt>{label}</dt>
      <dd className={num ? "num" : undefined}>{value}</dd>
    </div>
  );
}

/** The header under the name: badges, facts, and Lengkapi data paket while a field is empty. */
function PbjHeadFacts({ project: p, onEdit }: { project: PbjProjectDetail["project"]; onEdit: () => void }) {
  const missing = !p.instansi || !p.jenisPengadaan || p.pagu == null || !p.tahunAnggaran;
  return (
    <>
      <p className="pbj-badges">
        <Badge tone="info">{methodText(p)}</Badge>
        <Badge>{p.tahunAnggaran ? `TA ${p.tahunAnggaran}` : "TA belum diisi"}</Badge>
        {/* Kode RUP shows once it is filled; an empty one is in Lengkapi data paket. */}
        {p.kodeRup ? <Badge>{`Kode RUP ${p.kodeRup}`}</Badge> : null}
        <Badge tone={p.total > 0 && p.done === p.total ? "success" : "neutral"}>Progres {progressPct(p.done, p.total)}%</Badge>
      </p>
      <dl className="pk-facts">
        <Fact label="Jalur" value={routeText(p)} />
        <Fact label="Unit kerja" value={p.instansi || "Belum diisi"} empty={!p.instansi} />
        <Fact label="Jenis pengadaan" value={p.jenisPengadaan ? JENIS_LABEL[p.jenisPengadaan] : "Belum dipilih"} empty={!p.jenisPengadaan} />
        <Fact label="Pagu" value={formatRupiah(p.pagu)} empty={p.pagu == null} num />
        <Fact label="Aktivitas selesai" value={`${p.done} dari ${p.total}`} num />
        {p.keterangan ? (
          <div className="pk-fact pbj-fact-note">
            <dt>Keterangan paket</dt>
            <dd>{p.keterangan}</dd>
          </div>
        ) : null}
      </dl>
      {missing ? (
        <p className="pbj-complete">
          <Glyph name="infoCircle" size={16} />
          <span>Data paket belum lengkap.</span>
          <button type="button" className="pbj-complete-link" onClick={onEdit}>
            Lengkapi data paket
          </button>
        </p>
      ) : null}
    </>
  );
}

interface PbjFlowProps {
  detail: PbjProjectDetail;
  onChange: (next: PbjProjectDetail) => void;
}

/** Stepper, the viewed stage's activities, and Jadikan tahap berjalan. */
export function PbjFlow({ detail, onChange }: PbjFlowProps) {
  const { project, role } = detail;
  const [viewed, setViewed] = useState<PbjStage>(project.stage);
  const [mineOnly, setMineOnly] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const filterId = useId();
  const features = useFeatures();
  const flowOn = features.has("pbj_flow");
  const mergeOn = canMergeDocs(features);
  const docsOn = canActivityDocs(features);
  const group = detail.stages.find((s) => s.stage === viewed);
  const all = group?.activities ?? [];
  const shown = mineOnly ? all.filter((a) => a.canTick) : all;
  const mineCount = all.filter((a) => a.canTick).length;
  const outsideCount = all.filter((a) => a.outside).length;
  // Swakelola's pemilihan stages: no documents, never the current stage.
  const applies = pbjStageApplies(project.route, viewed);
  const names = useMemo(() => new Map(detail.stages.flatMap((s) => s.activities.map((a) => [a.id, a.activity] as const))), [detail.stages]);
  const nameOf = (id: string) => names.get(id);

  const setStage = async () => {
    setBusy(true);
    setError(null);
    try {
      onChange(await API.updatePbjProject(project.id, { stage: viewed }));
    } catch (err) {
      setError(API.errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  // One activity saved: the stage counts and the project progress follow
  // ("Di luar Pluto" activities never count).
  const saved = (a: PbjActivity) => {
    const stages = detail.stages.map((s) => {
      const activities = s.activities.map((x) => (x.id === a.id ? a : x));
      return { ...s, activities, done: activities.filter((x) => x.done && !x.outside).length };
    });
    onChange({ ...detail, stages, project: { ...project, done: stages.reduce((n, s) => n + s.done, 0) } });
  };

  return (
    <section className="pk-flow pbj-flow" aria-labelledby="pbj-stage-title">
      <nav className="pk-stepper" aria-label="Tahap PBJ">
        <ol className="pk-steps pbj-steps">
          {PBJ_STAGE_ORDER.map((stage, i) => {
            const g = detail.stages.find((s) => s.stage === stage);
            const state = stage === project.stage ? "current" : g && g.total > 0 && g.done === g.total ? "done" : undefined;
            return (
              <li key={stage} className="pk-step" data-state={state} aria-current={stage === project.stage ? "step" : undefined}>
                <button type="button" className="pbj-step-btn" aria-pressed={stage === viewed} onClick={() => setViewed(stage)}>
                  <span className="pk-step-dot num" aria-hidden="true">
                    {i + 1}
                  </span>
                  <span className="pk-step-label">{PBJ_STAGE_LABEL[stage]}</span>
                  <span className="pbj-step-count num">{stageCount(g)}</span>
                  {stage === project.stage ? <span className="pbj-step-now">Berjalan</span> : null}
                </button>
              </li>
            );
          })}
        </ol>
        <label className="pbj-step-select">
          <span className="visually-hidden">Lihat tahap</span>
          <select value={viewed} onChange={(e) => setViewed(e.currentTarget.value as PbjStage)}>
            {PBJ_STAGE_ORDER.map((stage, i) => (
              <option key={stage} value={stage}>
                Tahap {i + 1}: {PBJ_STAGE_LABEL[stage]}, {stageCount(detail.stages.find((s) => s.stage === stage))}
                {stage === project.stage ? " (berjalan)" : ""}
              </option>
            ))}
          </select>
        </label>
      </nav>

      <SequenceNote />

      <div className="pk-stage">
        <div className="pk-stage-head">
          <h2 id="pbj-stage-title" className="pk-stage-title">
            Tahap {PBJ_STAGE_LABEL[viewed]}
          </h2>
          {group && group.total > 0 ? (
            <span className="pk-stage-count num">
              {group.done} dari {group.total} selesai
            </span>
          ) : (
            <span className="pk-stage-count">Tidak berlaku</span>
          )}
        </div>
        <p className="pk-stage-hint">{PBJ_STAGE_HINT[viewed]}</p>
        {(flowOn && all.length > 0) || (mergeOn && applies && all.length > 0) ? (
          <div className="pbj-stage-tools">
            {flowOn && all.length > 0 ? (
              <a className="pbj-reg-link" href={stageFlowPath(project, viewed)}>
                <Glyph name="layers" size={16} />
                <span>Lihat alur tahap ini</span>
              </a>
            ) : null}
            {mergeOn && applies && all.length > 0 ? (
              <span className="pbj-stage-merge">
                <MergeDocsButton key={viewed} projectId={project.id} stage={viewed} label="Download gabungan dokumen tahap ini" size="sm" />
              </span>
            ) : null}
          </div>
        ) : null}
      </div>

      <StageSummary role={role} done={group?.done ?? 0} total={group?.total ?? 0} mineCount={mineCount} outsideCount={outsideCount} />

      <NextUp projectId={project.id} activity={all.find((a) => a.canTick && !a.done && !a.outside)} role={role} />

      {all.length > 0 ? (
        <div className="pbj-list-head">
          <h3 className="pbj-list-title">Aktivitas tahap ini</h3>
          <div className="pbj-filter">
            <Switch id={filterId} size="sm" checked={mineOnly} onChange={setMineOnly} label={`Hanya aktivitas peran saya (${mineCount})`} />
          </div>
        </div>
      ) : null}

      {all.length === 0 ? (
        <p className="pj-empty">
          {project.route === "swakelola" ? "Tahap ini tidak berlaku untuk jalur Swakelola: tidak ada pemilihan Penyedia. Ikuti alur tipe Swakelola paket ini." : "Belum ada aktivitas untuk tahap ini."}
        </p>
      ) : shown.length === 0 ? (
        <p className="pj-empty">Tidak ada aktivitas tahap ini yang pelakunya {ROLE_LABEL[role]}.</p>
      ) : (
        <div className="pbj-groups">
          {shown.some((a) => a.canTick) ? (
            <section className="pbj-group" aria-label={`Aktivitas Anda, tahap ${PBJ_STAGE_LABEL[viewed]}`}>
              <p className="pbj-group-title">
                Aktivitas Anda <span className="num">({shown.filter((a) => a.canTick).length})</span>
              </p>
              <ul className="pk-checklist" aria-label={`Aktivitas tahap ${PBJ_STAGE_LABEL[viewed]}`}>
                {shown
                  .filter((a) => a.canTick)
                  .map((a) => (
                    <ActivityRow key={a.id} projectId={project.id} activity={a} onSaved={saved} nameOf={nameOf} />
                  ))}
              </ul>
            </section>
          ) : null}
          {shown.some((a) => !a.canTick) ? (
            <section className="pbj-group" data-others="" aria-label={`Aktivitas pihak lain, tahap ${PBJ_STAGE_LABEL[viewed]}`}>
              <p className="pbj-group-title">
                Pihak lain <span className="num">({shown.filter((a) => !a.canTick).length})</span>
                <span className="pbj-group-hint">Hanya dapat dicentang oleh pelakunya</span>
              </p>
              <ul className="pk-checklist" aria-label={`Aktivitas pihak lain tahap ${PBJ_STAGE_LABEL[viewed]}`}>
                {shown
                  .filter((a) => !a.canTick)
                  .map((a) => (
                    <ActivityRow key={a.id} projectId={project.id} activity={a} onSaved={saved} nameOf={nameOf} />
                  ))}
              </ul>
            </section>
          ) : null}
        </div>
      )}

      {error ? (
        <Notice tone="danger" title="Tahap belum berubah">
          <p>{error}</p>
        </Notice>
      ) : null}

      {viewed === "identifikasi_kebutuhan" && docsOn ? <HandoverSection projectId={project.id} activities={all} canSend={role === "pa_kpa"} /> : null}

      {applies && viewed !== project.stage ? (
        <div className="pk-actions">
          <button type="button" className="btn btn-secondary" disabled={busy} aria-busy={busy || undefined} onClick={() => void setStage()}>
            <Glyph name="tickCircle" size={18} />
            <span>Jadikan tahap berjalan</span>
          </button>
        </div>
      ) : null}

      <p className="pbj-footnote">
        Ketentuan yang berlaku bergantung pada jenis, metode, nilai, dan kondisi paket. Mencentang aktivitas tidak otomatis menyatakan kepatuhan dan tidak menggantikan proses di SPSE, SiRUP, atau katalog elektronik.
      </p>
    </section>
  );
}

/** "3/9" for a stage, "Tidak berlaku" when the route has no activity in it. */
function stageCount(g: { done: number; total: number } | undefined): string {
  return g && g.total > 0 ? `${g.done}/${g.total}` : "Tidak berlaku";
}

/** The viewed stage's progress and what the current role (Mode) may do in it. */
function StageSummary({ role, done, total, mineCount, outsideCount }: { role: PbjProjectDetail["role"]; done: number; total: number; mineCount: number; outsideCount: number }) {
  const pct = progressPct(done, total);
  return (
    <aside className="pbj-summary" aria-label="Rangkuman tahap">
      <div className="pbj-summary-cell">
        <h3 className="pbj-subtitle visually-hidden">Rangkuman tahap</h3>
        {total > 0 ? (
          <>
            <span className="pbj-bar" role="progressbar" aria-label="Progres tahap" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct}>
              <span className="pbj-bar-fill" style={{ width: `${pct}%` }} />
            </span>
            <p className="pbj-next num">
              {done} dari {total} aktivitas selesai ({pct}%)
            </p>
            {outsideCount > 0 ? (
              <p className="pbj-next">
                {outsideCount} aktivitas di luar Pluto (oleh penyedia, peserta, atau pihak lain) tidak dihitung.
              </p>
            ) : null}
          </>
        ) : (
          <p className="pbj-next">Tahap tidak berlaku untuk jalur ini.</p>
        )}
      </div>
      <div className="pbj-summary-cell">
        <h3 className="pbj-subtitle visually-hidden">Akses peran saat ini</h3>
        <p className="pbj-access">
          {mineCount > 0 ? <Badge tone="success">Dapat mencentang sebagian aktivitas</Badge> : <Badge>Hanya melihat</Badge>}
        </p>
        <p className="pbj-role">
          <Glyph name="profile2user" size={18} />
          <span>
            Mode <strong>{ROLE_LABEL[role]}</strong>. Anda dapat mencentang aktivitas yang pelakunya {ROLE_LABEL[role]}; aktivitas lain hanya dapat dilihat.
          </span>
        </p>
      </div>
    </aside>
  );
}

/** The first activity of the viewed stage the current role still has to do. */
function NextUp({ projectId, activity: a, role }: { projectId: string; activity: PbjActivity | undefined; role: PbjProjectDetail["role"] }) {
  const features = useFeatures();
  if (!a) return null;
  return (
    <section className="pbj-nextup" aria-label="Berikutnya untuk Anda">
      <div className="pbj-nextup-text">
        <p className="pbj-nextup-label">Berikutnya untuk Anda ({ROLE_LABEL[role]})</p>
        <p className="pbj-nextup-title">{a.activity}</p>
        {a.output ? <p className="pbj-nextup-meta">Output: {a.output}</p> : null}
        {a.output ? <ActivityDocStatus activityKey={a.id} title={a.output} /> : null}
      </div>
      {a.output && canActivityDocs(features) ? (
        <span className="pbj-nextup-actions">
          <ActivityDocButtons activityKey={a.id} title={a.output} variant="card" />
        </span>
      ) : null}
    </section>
  );
}

function ActivityRow({ projectId, activity: a, onSaved, nameOf }: { projectId: string; activity: PbjActivity; onSaved: (a: PbjActivity) => void; nameOf: (id: string) => string | undefined }) {
  const id = useId();
  const features = useFeatures();
  const docx = useDocx();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const tick = async (done: boolean) => {
    setBusy(true);
    setError(null);
    try {
      onSaved(await API.tickPbjActivity(projectId, a.id, done));
    } catch (err) {
      setError(API.errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const status = a.done ? `Dicentang oleh ${a.doneRole ? ROLE_LABEL[a.doneRole] : "pelaku"}` : null;
  return (
    <li className="pk-item pbj-item" data-done={a.done ? "" : undefined} data-readonly={a.canTick ? undefined : ""} data-outside={a.outside ? "" : undefined}>
      <div className="pk-item-row">
        {a.outside ? (
          <p className="pk-check pbj-readonly">
            <span className="pbj-lock" title="Di luar Pluto">
              <Glyph name="link" size={18} />
            </span>
            <span className="pk-item-label">{a.activity}</span>
          </p>
        ) : a.canTick ? (
          <label className="pk-check" htmlFor={`${id}-done`}>
            <input id={`${id}-done`} type="checkbox" checked={a.done} disabled={busy} onChange={(e) => void tick(e.currentTarget.checked)} />
            <span className="pk-item-label">{a.activity}</span>
          </label>
        ) : (
          <p className="pk-check pbj-readonly">
            <span className="pbj-lock" title="Hanya pelakunya yang dapat mencentang">
              <Glyph name={a.done ? "tickCircle" : "lock"} size={18} />
            </span>
            <span className="pk-item-label">{a.activity}</span>
          </p>
        )}
        <div className="pk-item-actions">
          {a.output && canActivityDocs(features) ? (
            <>
              <ActivityDocButtons activityKey={a.id} title={a.output} />
              <ActivityDocUpload activityKey={a.id} title={a.output} onError={setUploadError} />
            </>
          ) : null}
          {/* Icon-only beside Detail, so a row with a saved version (five actions) stays on one line. */}
          {a.output && canDocx(features) ? (
            <button
              type="button"
              className="ui-icon-btn"
              disabled={docx.busy !== null}
              aria-busy={docx.busy !== null || undefined}
              title={docx.busy ? "Menyiapkan .docx" : `Unduh .docx: ${a.output}`}
              aria-label={`Unduh .docx ${a.output}`}
              onClick={() => void docx.start(a.id, () => API.downloadNodeDocx(a.id, projectId))}
            >
              <Glyph name="documentDownload" />
            </button>
          ) : null}
        </div>
        {/* Detail stays beside the name when the buttons wrap under it. */}
        <button type="button" className="ui-icon-btn pbj-item-info" aria-expanded={open} aria-controls={`${id}-detail`} aria-label={`Detail ${a.activity}`} title="Detail" onClick={() => setOpen((o) => !o)}>
          <Glyph name="infoCircle" />
        </button>
        {/* In the row's grid: under the name, and above the buttons when they wrap (pbj.css). */}
        <p className="pbj-item-meta">
          {a.outside ? <Badge>Di luar Pluto</Badge> : null}
          {a.type === "keputusan" ? <Badge tone="warning">Titik keputusan</Badge> : null}
          <span>{a.actor}</span>
          {a.output ? <span>Output: {a.output}</span> : null}
          {status ? <span className="pbj-done-by">{status}</span> : null}
          {a.output ? <ActivityDocStatus activityKey={a.id} title={a.output} /> : null}
        </p>
      </div>
      {a.outside ? <p className="pbj-item-note">{OUTSIDE_PLUTO_HINT}</p> : null}
      {open ? (
        <div id={`${id}-detail`} className="pbj-detail">
          <NodeFacts node={a} showActor={false} nameOf={nameOf} />
        </div>
      ) : null}
      {error || uploadError || docx.error ? (
        <p className="pk-item-error" role="alert">
          <Glyph name="danger" size={16} />
          <span>{error ?? uploadError ?? docx.error?.message}</span>
        </p>
      ) : null}
    </li>
  );
}
